import { createRoot } from 'react-dom/client';
import { isFunction } from 'lodash';
import classnames from 'classnames';

export default function CustomOverlay({
	appendToMap, position, className, component, map, offsetY = 0,
	disableIdleEvent, google, gesturesDisabled, idleDisabled, animationDelay
}) {
	this._animationDelay = animationDelay || 0;
	this._className = className;
	this._appendToMap = appendToMap;
	this._component = component;
	this._gesturesDisabled = gesturesDisabled;
	this._idleDisabled = idleDisabled;
	this._map = map;
	this._position = position;
	this._offsetY = offsetY;
	this._lastZIndex = null;
	this._setDisableMapIdleEvent = disableIdleEvent;
	this._lastPanOnOpen = null;
	this._google = google;
	this._root = null;

	if (isFunction(this.setMap)) {
		if (this._setDisableMapIdleEvent) {
			this._setDisableMapIdleEvent(true);
		}
		this.setMap(map);
	}
}

export function initialize(google) {
	CustomOverlay.prototype = new google.maps.OverlayView();

	CustomOverlay.prototype.updateComponent = function updateComponent(component) {
		if (this._div && this._root) {
			this._component = component;
			this._root.render(this._component);

			// If overlay is a window and not a pin, disable idle and fit to screen
			if (this._idleDisabled) {
				this.panMapToFitOverlay();
			}
		}
	};

	CustomOverlay.prototype.setZIndex = function setZIndex(zIndex) {
		if (this._div) {
			let newZIndex = zIndex;
			if (zIndex) {
				this._lastZIndex = this._div.style.zIndex;
			} else {
				newZIndex = this._lastZIndex;
			}

			this._div.style.zIndex = newZIndex;
		}
	};

	CustomOverlay.prototype.onAdd = function onAdd() {
		const div = document.createElement('div');
		div.className = classnames('overlay-container', this._className);
		this._div = div;
		const panes = this.getPanes();
		panes.floatPane.appendChild(div);
		this._root = createRoot(div);
		this._root.render(this._component);
		setTimeout(() => this._div.classList.add('mounted'), 0);

		if (this._gesturesDisabled) {
			this.disableGestures();
		}
	};

	CustomOverlay.prototype.draw = function draw() {
		this.setDivLocation();
	};

	CustomOverlay.prototype.onRemove = function onRemove() {
		if (this._div) {
			this.enableGestures();
			this._div.classList.remove('mounted');
			if (this._setDisableMapIdleEvent) {
				this._setDisableMapIdleEvent(false);
			}


			// Allow overlay to do any animations before removing
			setTimeout(() => {
				if (this._root) {
					this._root.unmount();
					this._root = null;
				}
				this._div.parentNode.removeChild(this._div);
				this._div = null;
			}, this._animationDelay);
		}
	};

	CustomOverlay.prototype.setDivLocation = function setDivLocation() {
		if (this._div) {
			const div = this._div;

			if (this._appendToMap) {
				this._map.getDiv().appendChild(div);
				div.style.left = '0px';
				div.style.bottom = '0px';
				div.style.width = '100%';
			} else {
				const overlayProjection = this.getProjection();
				const pixel = overlayProjection.fromLatLngToDivPixel(this._position);
				div.style.left = `${pixel.x - (div.clientWidth / 2)}px`;
				div.style.top = `${pixel.y - (div.clientHeight + (this._offsetY))}px`;
			}
		}
	};

	CustomOverlay.prototype.resetMapLocation = function resetMapLocation() {
		return new Promise((resolve) => {
			if (this._lastPanOnOpen) {
				const { x, y } = this._lastPanOnOpen;
				this._lastPanOnOpen = null;
				this._map.panBy(-1 * x, -1 * y);

				if (x || y) {
					// Wait for the idle event and then re-enable
					const handler = this._map.addListener('idle', () => {
						if (this._setDisableMapIdleEvent) {
							this._setDisableMapIdleEvent(false);
						}
						this._google.maps.event.removeListener(handler);
						resolve();
					});
				} else {
					resolve();
				}
			}
		});
	};

	CustomOverlay.prototype.panMapToFitOverlay = function panMapToFitOverlay() {
		if (this._appendToMap) {
			return;
		}

		if (this._div) {
			const divRect = this._div.getBoundingClientRect();
			const mapRect = this._map.getDiv().getBoundingClientRect();

			let y = 0;
			let x = 0;

			// Div too high to be visible on map
			if (divRect.top < mapRect.top) {
				y = (divRect.top - mapRect.top) - 70;
			}

			// Div too low
			if (divRect.bottom > mapRect.bottom) {
				y = (divRect.bottom - mapRect.bottom) + 10;
			}

			// Div too far left
			if (divRect.left < mapRect.left) {
				x = (divRect.left - mapRect.left) - 10;
			}

			// Div too far right
			if (divRect.right > mapRect.right) {
				x = (divRect.right - mapRect.right) + 10;
			}

			// Save the x,y we use to pan so we can reset when the overlay
			// is closed
			this._lastPanOnOpen = { x, y };
			this._map.panBy(x, y);
		}
	};

	CustomOverlay.prototype.disableGestures = function disableGestures() {
		this._map.setOptions({
			gestureHandling: 'none'
		});
	};

	CustomOverlay.prototype.enableGestures = function enableGestures() {
		this._map.setOptions({
			gestureHandling: 'greedy'
		});
	};
}
